/**
 * SequencerOverlay Component
 * Fullscreen overlay for building radio clock sequences with playlist slots
 */

import React from 'react';
import { X, Plus, Radio, Loader2 } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { useSequencerStore } from '../../stores/sequencer';
import { useWorkbenchStore } from '../../stores/workbench';
import { useGenerateSequence, usePreviewSequence } from '../../api/hooks/useSequencer';
import { useAddTracks, playlistKeys } from '../../api/hooks/usePlaylists';
import { SequencerSlot } from './SequencerSlot';
import { Button } from '../shared/Button';
import { createLogger } from '@/lib/logger';

const log = createLogger({ namespace: 'Sequencer' });

export interface SequencerOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}


export function SequencerOverlay({ isOpen, onClose }: SequencerOverlayProps) {
  const {
    slots,
    artistSeparation,
    iterations,
    stopMode,
    addSlot,
    removeSlot,
    addStopset,
    setArtistSeparation,
    setIterations,
    setStopMode,
    updateSlotMode,
    updateSlotRepeatCount,
    reset,
  } = useSequencerStore();

  const activePlaylist = useWorkbenchStore((state) => state.activePlaylist);

  // API mutations
  const queryClient = useQueryClient();
  const generateMutation = useGenerateSequence();
  const previewMutation = usePreviewSequence();
  const addTracksMutation = useAddTracks();

  if (!isOpen) return null;

  // Early return if no active playlist
  if (!activePlaylist) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-10 flex items-center justify-center" onClick={onClose}>
        <div className="bg-zinc-900 rounded-xl p-6 max-w-md mx-4 border border-zinc-800" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-zinc-100 mb-2">No Active Playlist</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Please open a playlist in the workbench before using the sequencer.
          </p>
          <Button variant="primary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // Convert slots to API format (snake_case for backend)
  // Filter out empty crate slots (stopsets are always included)
  // NOTE: We don't send output_playlist because we want to manually append tracks
  const buildApiRequest = () => {
    const validSlots = slots.filter(
      (slot) => slot.type === 'stopset' || (slot.type === 'crate' && slot.playlist !== null)
    );

    return {
      name: '', // Empty - not creating a new playlist
      slots: validSlots.map((slot) => ({
        type: slot.type,
        playlist_id: slot.playlist?.id ?? null,
        mode: slot.mode,
        repeat_count: slot.repeatCount,
      })),
      artist_separation: artistSeparation,
      iterations,
      stop_mode: stopMode,
      // DO NOT send output_playlist - we'll manually append tracks after generation
    };
  };

  // Handle generate button
  const handleGenerate = async () => {
    // Validate: at least one slot with a playlist
    const hasValidSlots = slots.some(
      (slot) => slot.type === 'crate' && slot.playlist !== null
    );

    if (!hasValidSlots) {
      log.warn('At least one slot must have a playlist');
      return;
    }

    // Validate: must have active playlist
    if (!activePlaylist?.id) {
      log.warn('No active playlist to add tracks to');
      return;
    }

    try {
      const request = buildApiRequest();
      log.debug('Generating sequence to append:', request);

      // Step 1: Generate sequence (returns tracks only, no playlist creation)
      const result = await generateMutation.mutateAsync(request);

      log.debug(`Generated ${result.totalTracks} tracks`);

      // Step 2: Extract track URIs
      const trackUris = result.tracks.map((track) => track.uri);

      if (trackUris.length === 0) {
        log.warn('No tracks generated');
        return;
      }

      // Step 3: Add tracks to the active playlist in Spotify
      log.debug(`Adding ${trackUris.length} tracks to playlist "${activePlaylist.name}" (${activePlaylist.id})`);

      await addTracksMutation.mutateAsync({
        playlistId: activePlaylist.id,
        data: { uris: trackUris }
      });

      log.debug(`Successfully appended ${trackUris.length} tracks to "${activePlaylist.name}"`);

      // Step 4: Invalidate queries to refresh the workbench with updated playlist
      await queryClient.invalidateQueries({
        queryKey: playlistKeys.detail(activePlaylist.id)
      });

      // Success: close overlay
      onClose();
    } catch (error) {
      log.error('Sequence generation failed:', error);
      // TODO: Show error toast
    }
  };

  // Handle preview button
  const handlePreview = async () => {
    try {
      const request = buildApiRequest();
      const result = await previewMutation.mutateAsync(request);

      // TODO: Show preview results in a toast or modal
      log.debug('Preview result:', result);
      log.debug(`Estimated ${result.estimatedTracks} tracks, ${Math.round(result.estimatedDuration / 60000)} minutes`);
      if (result.warnings.length > 0) {
        log.warn('Warnings:', result.warnings);
      }
    } catch (error) {
      log.error('Preview failed:', error);
      // TODO: Show error toast
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop - only covers the workbench area, not the library */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-10"
        style={{ left: 'clamp(16rem, 20vw, 24rem)' }}
        onClick={onClose}
      />

      {/* Overlay - positioned to the right, leaving library visible */}
      <div
        className="fixed top-0 bottom-0 right-0 z-20 flex flex-col overflow-hidden"
        style={{ left: 'clamp(16rem, 20vw, 24rem)' }}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 flex flex-col max-w-5xl mx-auto w-full p-6 min-h-0 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-orange-500/10">
                <Radio className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-zinc-100">
                  Add Sequence to Playlist
                </h2>
                <p className="text-sm text-zinc-500 mt-0.5 flex items-center gap-2">
                  Generate and append tracks to "{activePlaylist?.name}"
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                    Auto-saved
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors"
              aria-label="Close sequencer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Slots Section - scrollable area with fixed controls */}
          <div className="flex-1 flex flex-col min-h-0 mb-4">
            {/* Scrollable Slots Area */}
            <div className="flex-1 overflow-y-auto pr-2 -mr-2 mb-4">
              <SortableContext
                items={slots.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {slots.map((slot, idx) => (
                    <SequencerSlot
                      key={slot.id}
                      slot={slot}
                      position={idx + 1}
                      onRemove={() => removeSlot(slot.id)}
                      onModeChange={(mode) => updateSlotMode(slot.id, mode)}
                      onRepeatCountChange={(count) => updateSlotRepeatCount(slot.id, count)}
                    />
                  ))}

                  {slots.length === 0 && (
                    <div className="py-12 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-zinc-800 mb-4">
                        <Radio className="w-8 h-8 text-zinc-600" />
                      </div>
                      <p className="text-zinc-500 mb-2">No slots yet</p>
                      <p className="text-sm text-zinc-600">
                        Add slots to start building your sequence
                      </p>
                    </div>
                  )}
                </div>
              </SortableContext>
            </div>

            {/* Add Slot Controls - always visible */}
            <div className="flex gap-2">
              <button
                onClick={addSlot}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 hover:border-emerald-500/50 text-emerald-400 text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Slot
              </button>
              <button
                onClick={addStopset}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 hover:border-amber-500/50 text-amber-400 text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Break
              </button>
              <button
                onClick={reset}
                className="px-4 py-2.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 text-red-400 text-sm font-medium transition-colors"
                title="Clear all slots and reset configuration"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Configuration Section - Compact horizontal layout */}
          <div className="flex items-center gap-4 p-3 rounded-xl bg-zinc-900/90 border border-zinc-800 mb-4 flex-shrink-0">
            {/* Artist Separation - Stepper */}
            <div className="flex items-center gap-2" title="Minimum tracks between same artist">
              <label className="text-xs text-zinc-500 whitespace-nowrap">Artist Sep</label>
              <div className="flex items-center">
                <button
                  onClick={() => setArtistSeparation(Math.max(1, artistSeparation - 1))}
                  className="w-6 h-6 rounded-l bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-r-0"
                >
                  −
                </button>
                <span className="w-8 h-6 flex items-center justify-center text-xs font-mono text-emerald-400 bg-zinc-800/50 border-y border-zinc-700">
                  {artistSeparation}
                </span>
                <button
                  onClick={() => setArtistSeparation(Math.min(10, artistSeparation + 1))}
                  className="w-6 h-6 rounded-r bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-l-0"
                >
                  +
                </button>
              </div>
            </div>

            <div className="h-6 w-px bg-zinc-700/50" />

            {/* Iterations - Stepper */}
            <div className="flex items-center gap-2" title="Number of clock cycles">
              <label className="text-xs text-zinc-500">Cycles</label>
              <div className="flex items-center">
                <button
                  onClick={() => setIterations(Math.max(1, iterations - 1))}
                  className="w-6 h-6 rounded-l bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-r-0"
                >
                  −
                </button>
                <span className="w-8 h-6 flex items-center justify-center text-xs font-mono text-orange-400 bg-zinc-800/50 border-y border-zinc-700">
                  {iterations}
                </span>
                <button
                  onClick={() => setIterations(Math.min(99, iterations + 1))}
                  className="w-6 h-6 rounded-r bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors border border-zinc-700 border-l-0"
                >
                  +
                </button>
              </div>
            </div>

            <div className="h-6 w-px bg-zinc-700/50" />

            {/* Stop Mode - Segmented control */}
            <div className="flex items-center gap-2" title="When to stop generating">
              <label className="text-xs text-zinc-500">Stop</label>
              <div className="flex rounded-md overflow-hidden border border-zinc-700">
                <button
                  onClick={() => setStopMode('shortest')}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    stopMode === 'shortest'
                      ? 'bg-orange-500/20 text-orange-300'
                      : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
                  }`}
                >
                  First Empty
                </button>
                <button
                  onClick={() => setStopMode('all')}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors border-l border-zinc-700 ${
                    stopMode === 'all'
                      ? 'bg-orange-500/20 text-orange-300'
                      : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
                  }`}
                >
                  All Empty
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-900 border border-zinc-800 flex-shrink-0">
            <p className="text-xs text-zinc-500">
              {slots.length === 0
                ? 'Add slots to continue'
                : !slots.some((s) => s.type === 'crate' && s.playlist)
                  ? 'Drop playlists into slots to continue'
                  : !activePlaylist?.id
                    ? 'No active playlist to append to'
                    : `Ready to append ${slots.filter((s) => s.type === 'crate' && s.playlist).length} slot${slots.filter((s) => s.type === 'crate' && s.playlist).length > 1 ? 's' : ''} to "${activePlaylist.name}"`}
            </p>
            <div className="flex gap-3">
              <Button variant="secondary" size="md" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="outline"
                size="md"
                onClick={handlePreview}
                disabled={slots.length === 0 || previewMutation.isPending}
                isLoading={previewMutation.isPending}
              >
                {previewMutation.isPending ? 'Previewing...' : 'Preview'}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleGenerate}
                disabled={
                  generateMutation.isPending ||
                  !slots.some((s) => s.type === 'crate' && s.playlist) ||
                  !activePlaylist?.id
                }
                isLoading={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding to Playlist...
                  </>
                ) : (
                  'Add to Playlist'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
